import { Component } from '@angular/core';
import { AdminActionsDirective } from '@app/main/admin/directives/admin-actions.directive';
import { UserService } from '@shared/services/user.service';
import { IUpdateUser, IUser } from '@shared/interfaces';
import { SnackService } from '@shared/services/snack.service';
import { userFilters } from '@app/main/admin/components/admin-toolbar/admin-toolbar-filters';
import { DialogService, ExportService, GuardService, SCDService } from '@shared/services';
import { ModuleService } from '@shared/services/module.service';
import { scdToUserBaseConverter } from '@shared/interfaces/user/IUserBaseData';
import { AdminService } from '../../../admin.service';

@Component({
    selector: 'app-admin-toolbar-user',
    templateUrl: './admin-toolbar-user.component.html',
    styleUrls: ['./admin-toolbar-user.component.scss'],
    standalone: false
})
export class AdminToolbarUserComponent extends AdminActionsDirective {
    filters = userFilters;
    searchValue: string;

    constructor(
        private userService: UserService,
        private snack: SnackService,
        private scdService: SCDService,
        private guardService: GuardService,
        private exportService: ExportService,
        adminService: AdminService,
        dialogService: DialogService,
        moduleService: ModuleService
    ) {
        super(adminService, dialogService, moduleService);
    }

    get isMaster() {
        return this.guardService.isMaster;
    }

    protected async deleteUser(): Promise<void> {
        try {
            const result = await this.deleteDialog();
            if (!result) {
                return;
            }
        } catch (e) {
            throw new Error('Failed to confirm user deletion.', { cause: e });
        }

        const user = this.selectedItem as IUser;
        try {
            await this.userService.deleteUser(user);
            this.snack.openSnackBarSuccess(`User ${user.userData.fullName} deleted successfully.`);
            this.goToMainView();
        } catch (e) {
            this.snack.openSnackBarError(e.error.message);
        }
    }

    protected async updateUser(event): Promise<void> {
        this.disableSubmitter(event);
        try {
            const scdUser = await this.scdService.getUserFromSCDByGid(this.selectedItem.id);

            const item = {
                id: scdUser.id,
                userData: scdToUserBaseConverter.toApi(scdUser)
            } as IUpdateUser;

            const user = await this.userService.updateUsersFromScd([item]);
            this.snack.openSnackBarSuccess(`User ${user[0].userData.fullName} updated successfully.`);
            this.goToUser(user[0]);
        } catch (e) {
            this.snack.openSnackBarError(e.error.message);
        } finally {
            this.enableSubmitter(event);
        }
    }

    protected async updateAll(event: any): Promise<void> {
        this.disableSubmitter(event);
        try {
            let users = await Promise.all(this.data.map(e => this.scdService.getUserFromSCDByGid(e.id)));
            users = users.filter(e => e);

            const failedUsers: IUser[] = this.data.filter(e => !users.some(a => a.id === e.id));

            const items = users.map(scdUser => {
                const item = this.data.find(e => scdUser.id === e.id);

                return {
                    id: item.id,
                    userData: scdUser
                } as IUpdateUser;
            });
            await this.userService.updateUsersFromScd(items);

            if (failedUsers?.length) {
                this.snack.openSnackBarError(
                    `Could not update users, who probably left the company: ${failedUsers.map(e => e.userData.fullName).join(', ')}`
                );
            } else {
                this.snack.openSnackBarSuccess('Users updated successfully.');
            }

            this.goToMainView();
        } catch (e) {
            this.snack.openSnackBarError(e.error?.message || String(e.error));
        } finally {
            this.enableSubmitter(event);
        }
    }

    async exportUsers(): Promise<void> {
        return this.exportService.generateExcelForUsers(this.data);
    }
}
